import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // EYKO 07.10.2026: klikniecie miniatury otwiera powiekszenie na caly ekran.
    // Duze zdjecie na gorze bywa poza ekranem, wiec sama zmiana slajdu byla niewidoczna.
    this.addEventListener('click', this.#handleThumbnailZoom, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * EYKO: opens the zoom dialog when a gallery thumbnail is clicked.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleThumbnailZoom = (event) => {
    const zoomDialog = this.refs.zoomDialogComponent;
    if (!zoomDialog || !(event.target instanceof Element)) return;

    const thumbnail = event.target.closest('.slideshow-controls__thumbnail');
    if (!thumbnail || !this.contains(thumbnail) || thumbnail.closest('zoom-dialog')) return;

    const thumbnails = Array.from(this.querySelectorAll('.slideshow-controls__thumbnail')).filter(
      (element) => !element.closest('zoom-dialog')
    );
    const index = thumbnails.indexOf(thumbnail);
    if (index < 0) return;

    try {
      zoomDialog.open(index, /** @type {PointerEvent} */ (event));
    } catch (error) {
      // Leave the default behaviour (slide change) if the dialog cannot be opened.
    }
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
